import { MoreVert } from '@mui/icons-material';
import { Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteChannelGuide } from '~/api/channel-guides';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { ChannelGuideList__ChannelGuide as ChannelGuide } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  channelGuide: ChannelGuide;
  Icon?: ElementType;
}

export const Menu = ({
  buttonType = 'icon',
  channelGuide,
  Icon = MoreVert,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<ChannelGuide>();
  const channelGuideActionProps = mergeGridActionProps(
    moreTableActionProps(channelGuide),
    cellProps,
  );
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteChannelGuide] = useDeleteChannelGuide();

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('common.actions.actions')}
          onClick={channelGuideActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...channelGuideActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        <MoreDeleteAction
          tooltip={channelGuide.canDestroy.reasons?.fullMessages[0]}
          disabled={!channelGuide.canDestroy.value}
          {...moreActionProps}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            await deleteChannelGuide({
              variables: {
                input: {
                  channelGuideIds: [moreMenuProps.context.id],
                },
              },
            });
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.actions.cancel')}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.channel_guides.dialogs.delete.prompt')}
        title={t('pages.channel_guides.dialogs.delete.title')}
      />
    </>
  );
};
